import React, { useState, useEffect, useCallback } from 'react';
import './KitchenPage.css';
import BASE from '../api';
import { authFetch, clearToken } from '../auth';

function tableLabel(order) {
  if (order.table_number === 'out') return `外帶 #${order.takeout_seq ?? '?'}`;
  return `🪑 ${order.table_number}`;
}

function elapsed(createdAt) {
  const diff = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (diff < 60) return `${diff}秒`;
  if (diff < 3600) return `${Math.floor(diff / 60)}分`;
  return `${Math.floor(diff / 3600)}小時`;
}

function sizeLabel(item, size) {
  if (!size) return '';
  return size === 'M' ? `(${item.label_large || '大'})` : `(${item.label_small || '小'})`;
}

export default function KitchenPage({ kitchen }) {
  const [data, setData] = useState({ merged_items: [], pending_orders: [], done_orders: [] });
  const [lastFetch, setLastFetch] = useState(null);
  const [newOrderIds, setNewOrderIds] = useState(new Set());

  const fetchOrders = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE}/api/kitchen/${kitchen.toLowerCase()}`);
      const json = await res.json();
      setData(prev => {
        const prevIds = new Set(prev.pending_orders.map(o => o.id));
        const incoming = json.pending_orders.filter(o => !prevIds.has(o.id));
        if (incoming.length > 0) {
          setNewOrderIds(ids => {
            const next = new Set([...ids, ...incoming.map(o => o.id)]);
            setTimeout(() => setNewOrderIds(cur => {
              const s = new Set(cur);
              incoming.forEach(o => s.delete(o.id));
              return s;
            }), 4000);
            return next;
          });
        }
        return json;
      });
      setLastFetch(new Date());
    } catch (e) { /* silent */ }
  }, [kitchen]);

  useEffect(() => {
    fetchOrders();
    const t = setInterval(fetchOrders, 8000);
    return () => clearInterval(t);
  }, [fetchOrders]);

  const setItemStatus = async (menu_item_id, status) => {
    await authFetch(`${BASE}/api/kitchen/${kitchen.toLowerCase()}/items`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ menu_item_id, status }),
    });
    fetchOrders();
  };

  const undoOrder = async (orderId) => {
    await authFetch(`${BASE}/api/kitchen/${kitchen.toLowerCase()}/orders/${orderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'pending' }),
    });
    fetchOrders();
  };

  const kitchenLabel = kitchen === 'A' ? '小廚房' : '大廚房';
  const kitchenDesc = kitchen === 'A' ? '主食麵飯・湯類・小點' : '熱炒・青菜・冷盤・羹湯';
  const { merged_items, pending_orders, done_orders } = data;

  const activeMerged = merged_items.filter(i => i.agg_status === 'pending' || i.agg_status === 'cooking');
  const sentMerged = merged_items.filter(i => i.agg_status === 'ready' || i.agg_status === 'done');

  return (
    <div className="kitchen-page">
      <header className="kitchen-header">
        <div className="kitchen-header-inner">
          <h1 className="kitchen-title">🍳 {kitchenLabel}</h1>
          <button className="logout-btn" onClick={() => { clearToken(); window.location.replace('/'); }}>登出</button>
        </div>
      </header>

      <div className="kitchen-body">

        {/* ── 製作列表 ── */}
        <section className="kitchen-section">
          {merged_items.length === 0 ? (
            <div className="empty-state">目前沒有待處理的餐點 ✓</div>
          ) : (
            <div className="merged-list">

              {activeMerged.map(item => (
                <div key={item.menu_item_id}
                  className={`merged-row merged-${item.agg_status}`}>

                  <div className="merged-left">
                    <span className="merged-qty">×{item.total_quantity}</span>
                    <div className="merged-info">
                      <span className="merged-name">{item.name}</span>

                      {/* 大/小份量細目 */}
                      <div className="merged-sizes">
                        {item.sizes.map((s, i) => (
                          <span key={i} className="size-pill">
                            {s.label ? `${s.label} ×${s.total}` : `×${s.total}`}
                            <span className="size-tables">
                              {s.tables.map((t, j) => (
                                <span key={j} className={`table-tag${t.table_number === 'out' ? ' table-tag-out' : ''}`}>{t.table_number === 'out' ? `外帶#${t.takeout_seq ?? '?'}` : t.table_number}</span>
                              ))}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="merged-actions">
                    {item.agg_status === 'pending' && (
                      <button className="status-btn status-btn-pending"
                        onClick={() => setItemStatus(item.menu_item_id, 'cooking')}>
                        開始製作
                      </button>
                    )}
                    {item.agg_status === 'cooking' && (
                      <button className="status-btn status-btn-cooking"
                        onClick={() => setItemStatus(item.menu_item_id, 'ready')}>
                        完成
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {sentMerged.length > 0 && (
                <>
                  <div className="done-divider">已送出候餐</div>
                  {sentMerged.map(item => (
                    <div key={item.menu_item_id} className="merged-row merged-sent">
                      <span className="merged-qty sent-qty">×{item.total_quantity}</span>
                      <span className="merged-name sent-name">{item.name}</span>
                      <span className="sent-badge">→ 送菜端</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </section>

        {/* ── 訂單進度 ── */}
        {pending_orders.length > 0 && (
          <section className="kitchen-section">
            <h2 className="section-title">訂單進度</h2>
            <div className="order-grid">
              {pending_orders.map(order => {
                const total = order.items.length;
                const readyCount = order.items.filter(i => ['ready','done'].includes(i.item_status)).length;
                const cookingCount = order.items.filter(i => i.item_status === 'cooking').length;
                const pct = total > 0 ? Math.round((readyCount / total) * 100) : 0;
                return (
                  <div key={order.id}
                    className={`order-card ${newOrderIds.has(order.id) ? 'order-new' : ''}`}>
                    <div className="order-card-header">
                      <span className={`order-table${order.table_number === 'out' ? ' order-table-out' : ''}`}>{tableLabel(order)}</span>
                    </div>
                    <div className="progress-bar-wrap">
                      <div className="progress-bar" style={{ width: `${pct}%` }} />
                    </div>
                    <ul className="order-items-list">
                      {order.items.map(item => (
                        <li key={item.order_item_id}
                          className={`order-item-row item-${item.item_status}`}>
                          <span className="item-status-dot" />
                          <span>×{item.quantity} {item.name}{sizeLabel(item, item.size)}</span>
                        </li>
                      ))}
                    </ul>
                    {order.note && <div className="order-note">📝 {order.note}</div>}
                    <div className="order-footer">
                      <span className="order-progress-text">
                        {readyCount}/{total} 完成
                        {cookingCount > 0 && ` · ${cookingCount} 製作中`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── 已完成訂單 ── */}
        {done_orders.length > 0 && (
          <section className="kitchen-section">
            <h2 className="section-title section-title-done">已完成訂單</h2>
            <div className="order-grid">
              {done_orders.map(order => (
                <div key={order.id} className="order-card order-card-done">
                  <div className="order-card-header">
                    <span className={`order-table${order.table_number === 'out' ? ' order-table-out' : ''}`}>{tableLabel(order)}</span>
                    <button className="undo-btn" onClick={() => undoOrder(order.id)}>撤回</button>
                  </div>
                  <ul className="order-items-list">
                    {order.items.map(item => (
                      <li key={item.order_item_id} className="order-item-row item-done">
                        <span className="item-status-dot" />
                        <span>×{item.quantity} {item.name}{sizeLabel(item, item.size)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
